// src/components/settings/SettingsPage.jsx
/**
 * @file components/settings/SettingsPage.jsx
 * @description Complete settings page for BYOK management
 * 
 * Features:
 * - View plan and credits
 * - Toggle BYOK on/off
 * - Add/delete API keys for OpenAI, Anthropic, Google
 * - Test API key validity
 * 
 * Single-component design (no splitting needed for now)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChatSession } from '../../context/ChatSessionContext';
import { byok, billing, profile as appProfile } from '../../app-client';

import ProviderIcon from './ProviderIcon';

import ConfirmationDialog from '../common/ConfirmationDialog';

import styles from '../../styles/Settings.module.css';
import {
    ArrowLeft,
    User,
    CreditCard,
    Key,
    CheckCircle,
    XCircle,
    AlertCircle,
    Info,
    Loader,
    AlertTriangle,
    Eye,
    EyeOff,
    Copy,
    DollarSign,
    Zap,
    Globe,
} from 'lucide-react';

/**
 * Provider configuration for API key management
 */
const PROVIDERS = [
    {
        id: 'openrouter',
        name: 'OpenRouter',
        placeholder: 'sk-or-v1-...',
        helpText: 'Get your API key from openrouter.ai/keys',
    },
    {
        id: 'google',
        name: 'Google AI',
        placeholder: 'AIza...',
        helpText: 'Get your API key from ai.google.dev',
    },
    {
        id: 'openai',
        name: 'OpenAI',
        placeholder: 'sk-...',
        helpText: 'Get your API key from platform.openai.com/api-keys',
    },
    {
        id: 'anthropic',
        name: 'Anthropic',
        placeholder: 'sk-ant-...',
        helpText: 'Get your API key from console.anthropic.com',
    },
    {
        id: 'bedrock',
        name: 'AWS Bedrock',
        placeholder: 'ABSK... (Bedrock API key)',
        helpText: 'Bedrock bearer key for chat. Batch jobs use a cross-account role (below).',
        // Bedrock also takes a chat region and an optional batch role-assumption config.
        supportsRegion: true,
        supportsBatch: true,
    },
];

/**
 * Main Settings Page Component
 */
const SettingsPage = () => {
    // --- Context Hooks ---
    const { 
        userProfile, 
        isLoadingProfile, 
        profileError,
        refreshUserProfile 
    } = useAuth();
    
    const { setViewMode } = useChatSession();
    
    // --- Local State ---
    const [activeKeyForm, setActiveKeyForm] = useState(null); // openrouter | 'openai' | 'anthropic' | 'google' | null
    const [keyInputs, setKeyInputs] = useState({
        openrouter: '',
        google: '',
        openai: '',
        anthropic: '',
        bedrock: ''
    });
    // Bedrock-only extras: chat-key region, and the cross-account batch config.
    const [bedrockRegion, setBedrockRegion] = useState('');
    const [showBedrockBatchForm, setShowBedrockBatchForm] = useState(false);
    const [isSavingBatchCfg, setIsSavingBatchCfg] = useState(false);
    const [bedrockBatch, setBedrockBatch] = useState({
        role_arn: '', s3_bucket: '', region: '',
        s3_prefix: '', service_role_arn: '', external_id: ''
    });
    const [isTogglingBYOK, setIsTogglingBYOK] = useState(false);
    const [isSavingKey, setIsSavingKey] = useState(false);
    const [isDeletingKey, setIsDeletingKey] = useState(null);
    const [isTestingKey, setIsTestingKey] = useState(null);
    const [testResults, setTestResults] = useState({});
    const [actionError, setActionError] = useState(null);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [keyToDelete, setKeyToDelete] = useState(null);
    const [showKeyValues, setShowKeyValues] = useState({});
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [customAmount, setCustomAmount] = useState('');
    const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
    const [showApiKeyBanner, setShowApiKeyBanner] = useState(true);

    console.log('SettingsPage: Rendering settings page');

    // --- Detect checkout redirect status from URL params ---
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('checkout') === 'success') {
            setActionSuccess('Payment successful! Your credits have been added.');
            refreshUserProfile();
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('checkout') === 'cancelled') {
            setActionError('Checkout was cancelled.');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('plan') === 'success') {
            setActionSuccess('Plan activated. It can take a few seconds to show here.');
            refreshUserProfile();
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('plan') === 'cancelled') {
            setActionError('Plan checkout was cancelled.');
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Handler: Plan purchase / management (org ladder: Free / Team / Scale) ---
    const [isPlanBusy, setIsPlanBusy] = useState(false);
    const handleSubscribe = async (plan) => {
        setIsPlanBusy(true);
        setActionError(null);
        try {
            const { checkout_url } = await billing.subscribe(plan);
            window.location.href = checkout_url;
        } catch (error) {
            setActionError(error.message || 'Failed to start plan checkout.');
        } finally {
            setIsPlanBusy(false);
        }
    };
    const handleManagePlan = async () => {
        setIsPlanBusy(true);
        setActionError(null);
        try {
            const { portal_url } = await billing.portal();
            window.location.href = portal_url;
        } catch (error) {
            setActionError(error.message || 'Failed to open the billing portal.');
        } finally {
            setIsPlanBusy(false);
        }
    };

    // --- Clear messages after 5 seconds ---
    useEffect(() => {
        if (actionError || actionSuccess) {
            const timer = setTimeout(() => {
                setActionError(null);
                setActionSuccess(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [actionError, actionSuccess]);
    
    // --- Handler: Go back to chat ---
    const handleBack = () => {
        console.log('SettingsPage: Navigating back to chat');
        setViewMode('chat');
    };
    
    // --- Handler: Toggle BYOK ---
    const handleToggleBYOK = async (newValue) => {
        console.log(`SettingsPage: Toggling BYOK to ${newValue}`);
        
        // Check if enabling BYOK without any keys
        if (newValue && !userProfile?.apiKeys?.openai?.isSet && !userProfile?.apiKeys?.anthropic?.isSet && !userProfile?.apiKeys?.google?.isSet && !userProfile?.apiKeys?.openrouter?.isSet) {
            setActionError('You must add at least one API key before enabling BYOK');
            return;
        }
        
        setIsTogglingBYOK(true);
        setActionError(null);
        setActionSuccess(null);
        
        try {
            await appProfile.updatePreferences({ useOwnKey: newValue });
            await refreshUserProfile();
            
            setActionSuccess(
                newValue 
                    ? 'BYOK enabled! You are now using your own API keys.'
                    : 'BYOK disabled. You are now using app credits.'
            );
            console.log('SettingsPage: BYOK toggle successful');
        } catch (error) {
            console.error('SettingsPage: Failed to toggle BYOK:', error);
            setActionError(error.message || 'Failed to update BYOK preference');
        } finally {
            setIsTogglingBYOK(false);
        }
    };
    
    // --- Handler: Save API Key ---
    const handleSaveKey = async (provider) => {
        console.log(`SettingsPage: Saving ${provider} API key`);
        
        const apiKey = keyInputs[provider].trim();
        
        if (!apiKey) {
            setActionError('Please enter an API key');
            return;
        }
        
        setIsSavingKey(true);
        setActionError(null);
        setActionSuccess(null);
        
        // Bedrock chat keys are region-scoped; send the region alongside the key.
        const opts = provider === 'bedrock' && bedrockRegion.trim()
            ? { region: bedrockRegion.trim() }
            : {};

        try {
            await byok.save(provider, apiKey, opts);
            await refreshUserProfile();

            // Clear input and close form
            setKeyInputs(prev => ({ ...prev, [provider]: '' }));
            setActiveKeyForm(null);

            setActionSuccess(`${PROVIDERS.find(p => p.id === provider).name} API key saved successfully!`);
            console.log(`SettingsPage: ${provider} key saved successfully`);
        } catch (error) {
            console.error(`SettingsPage: Failed to save ${provider} key:`, error);
            setActionError(error.message || `Failed to save ${provider} API key`);
        } finally {
            setIsSavingKey(false);
        }
    };

    // --- Handler: Save Bedrock batch (cross-account role) config ---
    const handleSaveBedrockBatch = async () => {
        const { role_arn, s3_bucket, region } = bedrockBatch;
        if (!role_arn.trim() || !s3_bucket.trim() || !region.trim()) {
            setActionError('Role ARN, S3 bucket, and region are required for batch.');
            return;
        }
        setIsSavingBatchCfg(true);
        setActionError(null);
        setActionSuccess(null);
        try {
            // Trim every field; the backend reads role_arn/s3_bucket/region + optionals.
            const batchConfig = Object.fromEntries(
                Object.entries(bedrockBatch)
                    .map(([k, v]) => [k, v.trim()])
                    .filter(([, v]) => v)
            );
            await byok.save('bedrock', '', { batchConfig });
            await refreshUserProfile();
            setShowBedrockBatchForm(false);
            setActionSuccess('Bedrock batch configuration saved successfully!');
        } catch (error) {
            console.error('SettingsPage: Failed to save Bedrock batch config:', error);
            setActionError(error.message || 'Failed to save Bedrock batch config');
        } finally {
            setIsSavingBatchCfg(false);
        }
    };
    
    // --- Handler: Delete API Key ---
    const handleDeleteKey = async () => {
        if (!keyToDelete) return; // Safety check
        const provider = keyToDelete;
        console.log(`SettingsPage: Deleting ${provider} API key`);
        
        setIsDeletingKey(provider);
        setActionError(null);
        setActionSuccess(null);
        
        try {
            await byok.remove(provider);
            await refreshUserProfile();
            
            // Clear test result
            setTestResults(prev => {
                const updated = { ...prev };
                delete updated[provider];
                return updated;
            });
            
            setActionSuccess(`${PROVIDERS.find(p => p.id === provider).name} API key deleted`);
            console.log(`SettingsPage: ${provider} key deleted successfully`);
        } catch (error) {
            console.error(`SettingsPage: Failed to delete ${provider} key:`, error);
            setActionError(error.message || `Failed to delete ${provider} API key`);
        } finally {
            setIsDeletingKey(null);
            setKeyToDelete(null); // Close the modal on completion
        }
    };
    
    // --- Handler: Test API Key ---
    const handleTestKey = async (provider) => {
        console.log(`SettingsPage: Testing ${provider} API key`);
        
        setIsTestingKey(provider);
        setActionError(null);
        
        try {
            const result = await byok.test(provider);
            
            setTestResults(prev => ({
                ...prev,
                [provider]: result
            }));
            
            if (result.valid) {
                setActionSuccess(`${PROVIDERS.find(p => p.id === provider).name} API key is valid!`);
            } else {
                setActionError(result.error || `${PROVIDERS.find(p => p.id === provider).name} API key is invalid`);
            }
            
            console.log(`SettingsPage: ${provider} key test result:`, result);
        } catch (error) {
            console.error(`SettingsPage: Failed to test ${provider} key:`, error);
            setActionError(error.message || `Failed to test ${provider} API key`);
        } finally {
            setIsTestingKey(null);
        }
    };
    
    // --- Handler: Buy Credits ---
    const handleBuyCredits = async () => {
        const amount = selectedPackage || parseInt(customAmount);
        if (!amount || amount < 1 || amount > 100) {
            setActionError('Please select a credit package or enter a valid amount ($1-$100).');
            return;
        }

        setIsCreatingCheckout(true);
        setActionError(null);

        try {
            const { checkout_url } = await billing.checkout(amount);
            window.location.href = checkout_url;
        } catch (error) {
            setActionError(error.message || 'Failed to start checkout.');
        } finally {
            setIsCreatingCheckout(false);
        }
    };

    // --- Handler: Copy API Key ---
    const handleCopyApiKey = (key) => {
        navigator.clipboard.writeText(key);
        setActionSuccess('API key copied to clipboard!');
    };

    // --- Render: Loading State ---
    if (isLoadingProfile) {
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading settings...</p>
                </div>
            </div>
        );
    }
    
    // --- Render: Error State ---
    if (profileError && !userProfile) {
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.settingsHeader}>
                    <button onClick={handleBack} className={styles.backButton}>
                        <ArrowLeft size={20} />
                        Back to Chat
                    </button>
                </div>
                
                <div className={styles.errorContainer}>
                    <p className={styles.errorMessage}>
                        Failed to load settings: {profileError}
                    </p>
                    <button 
                        onClick={refreshUserProfile}
                        className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }
    
    // --- Main Render ---
    return (
        <div className={styles.settingsContainer}>
            {/* Header */}
            <div className={styles.settingsHeader}>
                <button onClick={handleBack} className={styles.backButton}>
                    <ArrowLeft size={20} />
                    Back to Chat
                </button>
                
                <h1 className={styles.settingsTitle}>Settings</h1>
                <p className={styles.settingsSubtitle}>
                    Manage your credits and API keys
                </p>
            </div>
            
            {/* Content */}
            <div className={styles.settingsContent}>
                
                {/* Action Messages */}
                {actionSuccess && (
                    <div className={`${styles.alertBox} ${styles.success}`}>
                        <CheckCircle size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertMessage}>{actionSuccess}</p>
                        </div>
                    </div>
                )}
                
                {actionError && (
                    <div className={`${styles.alertBox} ${styles.warning}`}>
                        <AlertCircle size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertMessage}>{actionError}</p>
                        </div>
                    </div>
                )}
                
                {/* User Profile Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <User size={20} />
                        Account Information
                    </h2>
                    
                    <div className={styles.profileGrid}>
                        <div className={styles.profileItem}>
                            <span className={styles.profileLabel}>Plan</span>
                            <span className={`${styles.tierBadge} ${styles[userProfile?.planTier || 'free']}`}>
                                {userProfile?.planTier || 'free'}
                            </span>
                            <span className={styles.formHelp}>
                                {{
                                    free: 'Free — 25M tokens/month, auto-routing (all 3 modes), basic insights.',
                                    team: 'Team — 300M tokens/month, full insights, audit log, email support.',
                                    scale: 'Scale — 3,000M tokens/month, priority support, higher rate limits.',
                                    enterprise: 'Enterprise — custom cap, SSO, SLA.',
                                }[userProfile?.planTier || 'free']}
                            </span>
                            <div className={styles.formActions}>
                                {(userProfile?.planTier || 'free') === 'free' && (
                                    <>
                                        <button type="button" className={styles.backButton} disabled={isPlanBusy}
                                                onClick={() => handleSubscribe('team')}>
                                            Upgrade to Team — $29/mo
                                        </button>
                                        <button type="button" className={styles.backButton} disabled={isPlanBusy}
                                                onClick={() => handleSubscribe('scale')}>
                                            Upgrade to Scale — $149/mo
                                        </button>
                                    </>
                                )}
                                {(userProfile?.planTier || 'free') === 'team' && (
                                    <button type="button" className={styles.backButton} disabled={isPlanBusy}
                                            onClick={() => handleSubscribe('scale')}>
                                        Upgrade to Scale — $149/mo
                                    </button>
                                )}
                                {(userProfile?.planTier || 'free') !== 'free' && (
                                    <button type="button" className={styles.backButton} disabled={isPlanBusy}
                                            onClick={handleManagePlan}>
                                        Manage subscription
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div className={styles.profileItem}>
                            <span className={styles.profileLabel}>Available Credits</span>
                            <span className={`${styles.creditsValue} ${parseFloat(userProfile?.usageCredits || 0) <= 100 ? styles.low : ''}`}>
                                {Math.round(parseFloat(userProfile?.usageCredits || 0))}
                                <span className={styles.creditsDollarValue}> (${(parseFloat(userProfile?.usageCredits || 0) / (userProfile?.creditsPerDollar || 1000)).toFixed(2)})</span>
                            </span>
                        </div>
                        
                        <div className={styles.profileItem}>
                            <span className={styles.profileLabel}>BYOK Status</span>
                            <span className={styles.profileValue}>
                                {userProfile?.useOwnKey ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                    </div>
                    
                    {/* Low Credits Warning */}
                    {!userProfile?.useOwnKey && parseFloat(userProfile?.usageCredits || 0) <= 100 && parseFloat(userProfile?.usageCredits || 0) > 0 && (
                        <div className={`${styles.alertBox} ${styles.warning}`}>
                            <AlertCircle size={20} />
                            <div className={styles.alertContent}>
                                <p className={styles.alertTitle}>Low Credits Warning</p>
                                <p className={styles.alertMessage}>
                                    You have only {Math.round(parseFloat(userProfile.usageCredits))} credits remaining. Consider enabling BYOK or buying credits.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {/* Out of Credits Error */}
                    {!userProfile?.useOwnKey && parseFloat(userProfile?.usageCredits || 0) <= 0 && (
                        <div className={`${styles.alertBox} ${styles.warning}`}>
                            <XCircle size={20} />
                            <div className={styles.alertContent}>
                                <p className={styles.alertTitle}>Out of Credits</p>
                                <p className={styles.alertMessage}>
                                    You have no credits remaining. Enable BYOK below to continue using the service with your own API keys.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* API Key Banner (one-time, on first account creation) */}
                {userProfile?.newApiKey && showApiKeyBanner && (
                    <div className={`${styles.alertBox} ${styles.info}`}>
                        <Key size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertTitle}>Your API Key</p>
                            <p className={styles.alertMessage}>
                                Save this key now. It will not be shown again.
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                <code style={{
                                    background: 'var(--bg-tertiary)',
                                    padding: '6px 10px',
                                    borderRadius: '4px',
                                    fontSize: '0.85em',
                                    wordBreak: 'break-all',
                                    flex: 1,
                                }}>
                                    {userProfile.newApiKey}
                                </code>
                                <button
                                    onClick={() => handleCopyApiKey(userProfile.newApiKey)}
                                    className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                >
                                    <Copy size={14} /> Copy
                                </button>
                            </div>
                            <button
                                onClick={() => setShowApiKeyBanner(false)}
                                style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                                I've saved it, dismiss
                            </button>
                        </div>
                    </div>
                )}

                {/* Buy Credits Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <Zap size={20} />
                        Buy Credits
                    </h2>

                    <p className={styles.sectionDescription}>
                        Purchase credits to use the AI router. {userProfile?.creditsPerDollar || 1000} credits = $1.00.
                    </p>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        {[5, 10, 20].map(amount => (
                            <button
                                key={amount}
                                onClick={() => { setSelectedPackage(amount); setCustomAmount(''); }}
                                className={`${styles.button} ${selectedPackage === amount ? styles.buttonPrimary : styles.buttonSecondary}`}
                                style={{ flex: '1', minWidth: '120px', padding: '12px', textAlign: 'center' }}
                            >
                                <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>${amount}</div>
                                <div style={{ fontSize: '0.85em', opacity: 0.8 }}>{(amount * (userProfile?.creditsPerDollar || 1000)).toLocaleString()} credits</div>
                            </button>
                        ))}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Or custom amount:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontWeight: 'bold' }}>$</span>
                            <input
                                type="number"
                                min="1"
                                max="100"
                                value={customAmount}
                                onChange={(e) => { setCustomAmount(e.target.value); setSelectedPackage(null); }}
                                placeholder="1-100"
                                className={styles.formInput}
                                style={{ width: '80px' }}
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleBuyCredits}
                        disabled={isCreatingCheckout || (!selectedPackage && !customAmount)}
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        style={{ width: '100%' }}
                    >
                        {isCreatingCheckout
                            ? <><Loader size={16} className="spinning" /> Processing...</>
                            : <>
                                <DollarSign size={16} />
                                {selectedPackage
                                    ? `Buy ${(selectedPackage * (userProfile?.creditsPerDollar || 1000)).toLocaleString()} credits for $${selectedPackage}`
                                    : customAmount
                                        ? `Buy ${(parseInt(customAmount) * (userProfile?.creditsPerDollar || 1000)).toLocaleString()} credits for $${customAmount}`
                                        : 'Select a package to continue'}
                              </>
                        }
                    </button>
                </div>

                {/* BYOK Toggle Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <CreditCard size={20} />
                        Bring Your Own Key (BYOK)
                    </h2>
                    
                    <p className={styles.sectionDescription}>
                        Use your own API keys instead of app credits. When enabled, you'll be charged directly by the AI providers for usage.
                    </p>
                    
                    <div className={styles.byokToggleContainer}>
                        <div className={styles.byokToggleInfo}>
                            <p className={styles.byokToggleTitle}>Use My API Keys</p>
                            <p className={styles.byokToggleDescription}>
                                {userProfile?.useOwnKey 
                                    ? 'You are currently using your own API keys'
                                    : 'You are currently using app credits'}
                            </p>
                        </div>
                        
                        <label className={styles.toggleSwitch}>
                            <input 
                                type="checkbox"
                                checked={userProfile?.useOwnKey || false}
                                onChange={(e) => handleToggleBYOK(e.target.checked)}
                                disabled={isTogglingBYOK}
                            />
                            <span className={styles.toggleSlider}></span>
                        </label>
                    </div>
                    
                    {/* Info Box */}
                    <div className={`${styles.alertBox} ${styles.info}`}>
                        <Info size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertMessage}>
                                {userProfile?.useOwnKey 
                                    ? 'You are being billed directly by the AI providers. Your app credits are not being used.'
                                    : 'Add at least one API key below before enabling BYOK. Your API keys are encrypted and never visible after saving.'}
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Web Search Section — native provider search */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <Globe size={20} />
                        Web Search
                    </h2>

                    <p className={styles.sectionDescription}>
                        Web search runs <strong>natively on the model's own provider</strong>: send{' '}
                        <code>plugins: [&#123; "id": "web" &#125;]</code> and the serving provider's
                        built-in search grounds the answer — the model decides when to search and
                        the provider executes it. No search keys, no extra setup.
                    </p>

                    <div className={`${styles.alertBox} ${styles.info}`}>
                        <Info size={20} />
                        <div className={styles.alertContent}>
                            <p className={styles.alertTitle}>Model-dependent</p>
                            <p className={styles.alertMessage}>
                                Models whose provider has no built-in search answer from their
                                training data instead — the request still succeeds, without
                                grounding. Search usage is billed by the provider on the serving
                                key like any other token usage.
                            </p>
                        </div>
                    </div>
                </div>

                {/* API Keys Section */}
                <div className={styles.settingsSection}>
                    <h2 className={styles.sectionTitle}>
                        <Key size={20} />
                        API Key Management
                    </h2>
                    
                    <p className={styles.sectionDescription}>
                        Add your API keys from supported AI providers. Keys are encrypted and stored securely.
                    </p>
                    
                    <div className={styles.apiKeysGrid}>
                        {PROVIDERS.map(provider => {
                            const hasKey = userProfile?.apiKeys?.[provider.id]?.isSet || false;
                            const testResult = testResults[provider.id];
                            
                            return (
                                <div key={provider.id} className={styles.apiKeyCard}>
                                    <div className={`${styles.apiKeyIcon} ${styles[provider.id]}`}>
                                        <ProviderIcon providerId={provider.id} size={24} />
                                    </div>
                                    
                                    <div className={styles.apiKeyInfo}>
                                        <p className={styles.apiKeyProvider}>{provider.name}</p>
                                        <p className={`${styles.apiKeyStatus} ${hasKey ? styles.configured : styles.notConfigured}`}>
                                            {hasKey ? '✓ Key configured' : '○ No key configured'}
                                            {testResult && testResult.valid && ' (Valid)'}
                                            {testResult && !testResult.valid && ' (Invalid)'}
                                        </p>
                                    </div>
                                    
                                    <div className={styles.apiKeyActions}>
                                        {hasKey ? (
                                            <>
                                                <button
                                                    onClick={() => handleTestKey(provider.id)}
                                                    disabled={isTestingKey === provider.id}
                                                    className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                                >
                                                    {isTestingKey === provider.id ? <Loader size={16} className="spinning" /> : 'Test'}
                                                </button>
                                                <button
                                                    onClick={() => setKeyToDelete(provider.id)}
                                                    disabled={isDeletingKey === provider.id}
                                                    className={`${styles.button} ${styles.buttonDanger} ${styles.buttonSmall}`}
                                                >
                                                    {isDeletingKey === provider.id ? <Loader size={16} className="spinning" /> : 'Delete'}
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => setActiveKeyForm(provider.id)}
                                                className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                                            >
                                                Add Key
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Key Input Form */}
                                    {activeKeyForm === provider.id && (
                                        <div className={styles.keyForm}>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>API Key</label>
                                                <div className={styles.inputWrapper}>
                                                    <input
                                                        type={showKeyValues[provider.id] ? 'text' : 'password'}
                                                        value={keyInputs[provider.id]}
                                                        onChange={(e) => setKeyInputs(prev => ({
                                                            ...prev,
                                                            [provider.id]: e.target.value
                                                        }))}
                                                        placeholder={provider.placeholder}
                                                        className={styles.formInput}
                                                        disabled={isSavingKey}
                                                    />
                                                    <button
                                                        type="button"
                                                        className={styles.eyeButton}
                                                        onClick={() => setShowKeyValues(prev => ({ ...prev, [provider.id]: !prev[provider.id] }))}
                                                        aria-label={showKeyValues[provider.id] ? 'Hide key' : 'Show key'}
                                                        tabIndex={-1}
                                                    >
                                                        {showKeyValues[provider.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                                <p className={styles.formHelp}>{provider.helpText}</p>
                                            </div>

                                            {provider.supportsRegion && (
                                                <div className={styles.formGroup}>
                                                    <label className={styles.formLabel}>Region</label>
                                                    <input
                                                        type="text"
                                                        value={bedrockRegion}
                                                        onChange={(e) => setBedrockRegion(e.target.value)}
                                                        placeholder="us-east-1"
                                                        className={styles.formInput}
                                                        disabled={isSavingKey}
                                                    />
                                                    <p className={styles.formHelp}>AWS region where your Bedrock models are enabled.</p>
                                                </div>
                                            )}

                                            <div className={styles.formActions}>
                                                <button
                                                    onClick={() => {
                                                        setActiveKeyForm(null);
                                                        setKeyInputs(prev => ({ ...prev, [provider.id]: '' }));
                                                    }}
                                                    className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                                    disabled={isSavingKey}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleSaveKey(provider.id)}
                                                    className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                                                    disabled={isSavingKey || !keyInputs[provider.id].trim()}
                                                >
                                                    {isSavingKey ? <Loader size={16} className="spinning" /> : 'Save Key'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Bedrock batch (cross-account role) config — a separate
                                        save path from the chat bearer key above. */}
                                    {provider.supportsBatch && (
                                        <div className={styles.keyForm}>
                                            {!showBedrockBatchForm ? (
                                                <button
                                                    onClick={() => setShowBedrockBatchForm(true)}
                                                    className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                                >
                                                    Configure batch (cross-account role)
                                                </button>
                                            ) : (
                                                <>
                                                    <p className={styles.formHelp}>
                                                        Batch jobs run in your AWS account via an assumed role.
                                                        Trust the Aridel account (640351538273) with an ExternalId
                                                        in your role&apos;s trust policy.
                                                    </p>
                                                    {[
                                                        { k: 'role_arn', label: 'Role ARN *', ph: 'arn:aws:iam::<acct>:role/aridel-batch' },
                                                        { k: 's3_bucket', label: 'S3 bucket *', ph: 'my-bedrock-batch-bucket' },
                                                        { k: 'region', label: 'Region *', ph: 'us-east-1' },
                                                        { k: 's3_prefix', label: 'S3 prefix', ph: 'aridel-batches/' },
                                                        { k: 'service_role_arn', label: 'Service role ARN', ph: 'arn:aws:iam::<acct>:role/bedrock-batch-service' },
                                                        { k: 'external_id', label: 'External ID', ph: 'optional-shared-secret' },
                                                    ].map(f => (
                                                        <div key={f.k} className={styles.formGroup}>
                                                            <label className={styles.formLabel}>{f.label}</label>
                                                            <input
                                                                type="text"
                                                                value={bedrockBatch[f.k]}
                                                                onChange={(e) => setBedrockBatch(prev => ({ ...prev, [f.k]: e.target.value }))}
                                                                placeholder={f.ph}
                                                                className={styles.formInput}
                                                                disabled={isSavingBatchCfg}
                                                            />
                                                        </div>
                                                    ))}
                                                    <div className={styles.formActions}>
                                                        <button
                                                            onClick={() => setShowBedrockBatchForm(false)}
                                                            className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                                                            disabled={isSavingBatchCfg}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={handleSaveBedrockBatch}
                                                            className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonSmall}`}
                                                            disabled={isSavingBatchCfg}
                                                        >
                                                            {isSavingBatchCfg ? <Loader size={16} className="spinning" /> : 'Save batch config'}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <ConfirmationDialog
                isOpen={!!keyToDelete}
                onClose={() => setKeyToDelete(null)}
                onConfirm={handleDeleteKey}
                title="Delete API Key"
                message={`Are you sure you want to delete the key for "<strong>${keyToDelete ? PROVIDERS.find(p => p.id === keyToDelete)?.name : ''}</strong>"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                isProcessing={!!isDeletingKey}
                error={actionError}
                variant="danger"
            />
        </div>
    );
};

export default SettingsPage;